import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OGC_TILES_BASE_URL } from '@angular-workspace/services';
import { firstValueFrom } from 'rxjs';

import {
  parseBboxItemsResponse,
  type OgcBbox,
} from '../bbox/canvas-bbox.utils';

const BBOX_CRS = 'http://www.opengis.net/def/crs/OGC/1.3/CRS84';
const ITEMS_PAGE_LIMIT = 1000;
const MAX_MATCHED_FEATURES = 1_000_000;

export interface CanvasOgcCollectionLink {
  href?: string;
  rel?: string;
  type?: string;
}

export interface CanvasOgcCollection {
  id?: string;
  itemType?: string;
  title?: string;
  links?: CanvasOgcCollectionLink[];
  [key: string]: unknown;
}

export interface CanvasOgcFeature {
  type: string;
  id?: string;
  geometry?: { type?: string; coordinates?: unknown };
  properties?: Record<string, unknown>;
  links?: CanvasOgcCollectionLink[];
}

export interface CanvasOgcFeatureCollection {
  type: string;
  features: CanvasOgcFeature[];
  numberMatched?: number;
  numberReturned?: number;
  links?: CanvasOgcCollectionLink[];
}

export interface CanvasOgcCollectionsResponse {
  collections?: CanvasOgcCollection[];
  links?: CanvasOgcCollectionLink[];
}

export interface CanvasBboxSearchHit {
  resourceId: string;
  label: string;
  collectionId: string;
  coordinate: [number, number];
  properties?: Record<string, unknown>;
}

/** OGC Features API client for canvas bbox search and filter flows. */
@Injectable({ providedIn: 'root' })
export class CanvasOgcFeaturesApiService {
  private readonly http = inject(HttpClient);
  private readonly centralOgcBaseUrl =
    inject(OGC_TILES_BASE_URL, { optional: true }) ?? '';

  async getCollections(
    serverBaseUrl?: string,
  ): Promise<CanvasOgcCollectionsResponse> {
    const base = this.normalizeBaseUrl(serverBaseUrl ?? this.centralOgcBaseUrl);
    if (!base) {
      throw new Error('OGC server URL is not configured');
    }

    const headers = new HttpHeaders({
      Accept: 'application/json, text/plain, */*',
    });
    return firstValueFrom(
      this.http.get<CanvasOgcCollectionsResponse>(`${base}/collections`, {
        headers,
      }),
    );
  }

  /**
   * Bbox search across central OGC record collections (ui-canvas `performBboxSearch` flow).
   * 1. GET /collections
   * 2. Prefer collections with itemType === 'record'
   * 3. For each → GET /collections/{id}/items?bbox=…
   */
  async searchResourcesByBbox(
    bbox: OgcBbox,
    options?: { allowedResourceIds?: Set<string>; serverBaseUrl?: string },
  ): Promise<CanvasBboxSearchHit[]> {
    const base = this.normalizeBaseUrl(
      options?.serverBaseUrl ?? this.centralOgcBaseUrl,
    );
    if (!base) {
      throw new Error('OGC server URL is not configured');
    }

    const collectionsResponse = await this.getCollections(base);
    const allCollections = collectionsResponse.collections ?? [];
    if (allCollections.length === 0) {
      return [];
    }

    const recordCollections = allCollections.filter(
      (c) => String(c.itemType ?? '').toLowerCase() === 'record',
    );
    const collectionsToQuery =
      recordCollections.length > 0 ? recordCollections : allCollections;

    const hits: CanvasBboxSearchHit[] = [];
    const seenResourceIds = new Set<string>();
    const errors: string[] = [];

    for (const collection of collectionsToQuery) {
      const collectionId = String(collection.id ?? '').trim();
      if (!collectionId) continue;

      let features: CanvasOgcFeature[] = [];
      try {
        features = await this.fetchRecordCollectionItemsByBbox(
          collectionId,
          bbox,
          base,
        );
      } catch (err: unknown) {
        const msg = this.errorMessage(err);
        errors.push(`${collectionId}: ${msg}`);
        continue;
      }

      const parsed = parseBboxItemsResponse(features, { bbox });
      for (const resource of parsed) {
        if (seenResourceIds.has(resource.resourceId)) continue;
        if (
          options?.allowedResourceIds &&
          !options.allowedResourceIds.has(resource.resourceId)
        ) {
          continue;
        }

        seenResourceIds.add(resource.resourceId);
        hits.push({
          resourceId: resource.resourceId,
          label: resource.label,
          collectionId,
          coordinate: resource.coordinate,
          properties: resource.properties,
        });
      }
    }

    if (hits.length === 0 && errors.length === collectionsToQuery.length) {
      throw new Error(
        errors[0] ?? 'Failed to query OGC record collection items.',
      );
    }

    return hits;
  }

  /**
   * Query items in a record (discovery) collection — matches ui-canvas endpoint order.
   * Central OGC server rejects `bbox-crs` on these endpoints (400); omit it here.
   */
  private async fetchRecordCollectionItemsByBbox(
    collectionId: string,
    bbox: OgcBbox,
    base: string,
  ): Promise<CanvasOgcFeature[]> {
    const bboxString = bbox.join(',');
    const attempts: HttpParams[] = [
      new HttpParams()
        .set('bbox', bboxString)
        .set('limit', String(ITEMS_PAGE_LIMIT)),
      new HttpParams().set('bbox', bboxString),
    ];

    let lastError: unknown;
    for (const params of attempts) {
      try {
        const page = await this.getCollectionItems(collectionId, base, params);
        return page.features ?? [];
      } catch (err: unknown) {
        lastError = err;
      }
    }

    throw lastError ?? new Error('Failed to fetch record collection items.');
  }

  /**
   * Paginated fetch of all items in a collection on a resource server.
   * Used by Get All and Bbox filter (same endpoint, different query params).
   */
  async fetchAllCollectionItems(
    collectionId: string,
    serverBaseUrl: string,
    queryParams: Record<string, string> = {},
    options?: { signal?: AbortSignal },
  ): Promise<CanvasOgcFeature[]> {
    const base = this.normalizeBaseUrl(serverBaseUrl);
    if (!base) {
      throw new Error('Resource server URL is not configured');
    }

    const id = collectionId.trim();
    if (!id) {
      throw new Error('Collection id is required');
    }

    const headers = new HttpHeaders({
      Accept: 'application/json, text/plain, */*',
    });
    const hasBbox = Boolean(queryParams['bbox']?.trim());

    if (hasBbox) {
      const paramSets = this.buildBboxParamSets(queryParams);
      let lastError: unknown;
      for (const initialParams of paramSets) {
        try {
          return await this.fetchAllItemsPaginated(
            base,
            id,
            headers,
            initialParams,
            options?.signal,
          );
        } catch (err: unknown) {
          lastError = err;
          if (!this.isHttpStatus(err, 400)) {
            throw err;
          }
        }
      }
      throw lastError ?? new Error('Failed to fetch collection items.');
    }

    const params = this.queryParamsToHttpParams(queryParams);
    return this.fetchAllItemsPaginated(
      base,
      id,
      headers,
      params,
      options?.signal,
    );
  }

  /**
   * Bbox filter for one dataset on its resource server (paginated items fetch).
   */
  async fetchAllItemsByBbox(
    collectionId: string,
    bbox: OgcBbox,
    serverBaseUrl: string,
    options?: { signal?: AbortSignal },
  ): Promise<CanvasOgcFeature[]> {
    return this.fetchAllCollectionItems(
      collectionId,
      serverBaseUrl,
      { bbox: bbox.join(',') },
      options,
    );
  }

  private buildBboxParamSets(
    queryParams: Record<string, string>,
  ): HttpParams[] {
    const bboxString = queryParams['bbox'];
    const other = { ...queryParams };
    delete other['bbox'];

    const withCrs = this.queryParamsToHttpParams(other)
      .set('bbox', bboxString)
      .set('bbox-crs', BBOX_CRS);
    const withoutCrs = this.queryParamsToHttpParams(other).set(
      'bbox',
      bboxString,
    );
    return [withCrs, withoutCrs];
  }

  private queryParamsToHttpParams(
    queryParams: Record<string, string>,
  ): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(queryParams)) {
      const trimmed = String(value ?? '').trim();
      if (trimmed) {
        params = params.set(key, trimmed);
      }
    }
    return params;
  }

  private async fetchAllItemsPaginated(
    base: string,
    collectionId: string,
    headers: HttpHeaders,
    initialParams: HttpParams,
    signal?: AbortSignal,
  ): Promise<CanvasOgcFeature[]> {
    let url: string | null =
      `${base}/collections/${encodeURIComponent(collectionId)}/items?offset=1`;
    let params: HttpParams | undefined = initialParams;

    const allFeatures: CanvasOgcFeature[] = [];
    let totalMatched = 0;

    while (url) {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const response: CanvasOgcFeatureCollection = await firstValueFrom(
        this.http.get<CanvasOgcFeatureCollection>(url, {
          headers,
          params: params ?? undefined,
          ...(signal ? { signal } : {}),
        }),
      );

      params = undefined;
      const batch = response.features ?? [];
      allFeatures.push(...batch);

      const matched = response.numberMatched ?? batch.length;
      totalMatched = Math.max(totalMatched, matched);

      if (
        allFeatures.length >= totalMatched ||
        allFeatures.length >= MAX_MATCHED_FEATURES
      ) {
        break;
      }

      const nextLink = (response.links ?? []).find(
        (link: CanvasOgcCollectionLink) =>
          String(link.rel ?? '').toLowerCase() === 'next',
      );
      url = nextLink?.href?.trim() || null;
    }

    return allFeatures;
  }

  /** Dataset/resource-server items query (bbox filter). Tries with bbox-crs first. */
  async fetchItemsByBbox(
    collectionId: string,
    bbox: OgcBbox,
    serverBaseUrl?: string,
    options?: { limit?: number; includeBboxCrs?: boolean },
  ): Promise<CanvasOgcFeatureCollection> {
    const base = this.normalizeBaseUrl(serverBaseUrl ?? this.centralOgcBaseUrl);
    if (!base) {
      throw new Error('OGC server URL is not configured');
    }
    const id = collectionId.trim();
    if (!id) {
      throw new Error('Collection id is required');
    }

    const includeBboxCrs = options?.includeBboxCrs ?? true;
    const attempts: HttpParams[] = [];

    let withCrs = new HttpParams().set('bbox', bbox.join(','));
    if (includeBboxCrs) {
      withCrs = withCrs.set('bbox-crs', BBOX_CRS);
    }
    if (options?.limit != null) {
      withCrs = withCrs.set('limit', String(options.limit));
    }
    attempts.push(withCrs);

    let withoutCrs = new HttpParams().set('bbox', bbox.join(','));
    if (options?.limit != null) {
      withoutCrs = withoutCrs.set('limit', String(options.limit));
    }
    if (includeBboxCrs) {
      attempts.push(withoutCrs);
    }

    let lastError: unknown;
    for (const params of attempts) {
      try {
        return await this.getCollectionItems(id, base, params);
      } catch (err: unknown) {
        lastError = err;
      }
    }

    throw lastError ?? new Error('Failed to fetch collection items.');
  }

  private getCollectionItems(
    collectionId: string,
    base: string,
    params: HttpParams,
  ): Promise<CanvasOgcFeatureCollection> {
    const url = `${base}/collections/${encodeURIComponent(collectionId)}/items`;
    const headers = new HttpHeaders({
      Accept: 'application/json, text/plain, */*',
    });
    return firstValueFrom(
      this.http.get<CanvasOgcFeatureCollection>(url, { headers, params }),
    );
  }

  private isHttpStatus(err: unknown, status: number): boolean {
    const code = (err as { status?: number })?.status;
    return code === status;
  }

  private errorMessage(err: unknown): string {
    const e = err as {
      error?: { detail?: string };
      message?: string;
      status?: number;
    };
    if (e?.error?.detail) return String(e.error.detail);
    if (e?.message) return String(e.message);
    if (e?.status) return `HTTP ${e.status}`;
    return 'Request failed';
  }

  /**
   * Validates map tiles endpoint and returns tile collection URL template.
   * ui-canvas: GET `{server}/collections/{id}/map/tiles/WorldCRS84Quad`
   */
  async getTileLayerUrl(
    collectionId: string,
    serverBaseUrl: string,
  ): Promise<string> {
    const base = this.normalizeBaseUrl(serverBaseUrl);
    if (!base) {
      throw new Error('Tile server URL is not configured');
    }
    const id = collectionId.trim();
    if (!id) {
      throw new Error('Collection id is required');
    }

    const url = `${base}/collections/${encodeURIComponent(id)}/map/tiles/WorldCRS84Quad`;
    const headers = new HttpHeaders({
      Accept: 'application/json, text/plain, */*',
    });

    await firstValueFrom(
      this.http.get(url, { headers, observe: 'response' }),
    );

    return url;
  }

  private normalizeBaseUrl(url: string): string {
    let t = String(url ?? '').trim();
    if (!t) return '';
    if (!/^https?:\/\//i.test(t)) {
      t = `https://${t}`;
    }
    return t.replace(/\/+$/, '');
  }
}
